<template>
  <BaseModal
    :model-value="open"
    :show-close-button="false"
    size="small"
    :overlay-z-index="OVERLAY_Z_CONFIRM_DIALOG"
    aria-label="Notification record"
    @update:model-value="onModel"
  >
    <div
      class="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/20"
    >
      <i class="ri-error-warning-line text-2xl text-amber-400" aria-hidden="true" />
    </div>
    <h3
      class="mb-3 text-center text-lg font-semibold text-text"
      data-testid="notification-target-title"
    >
      {{ copy.title }}
    </h3>
    <div class="mb-4 border-b border-border" aria-hidden="true" />
    <p class="text-center text-sm text-text-secondary" data-testid="notification-target-message">
      {{ copy.message }}
    </p>

    <template #footer>
      <BaseButton
        variant="primary"
        class="w-full"
        data-testid="notification-target-ok"
        @click="close"
      >
        OK
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
/**
 * NOTIF-008 — the "record not found / deleted" modal a dead notification link shows instead
 * of a page that cannot show the record. Mounted once, with the bell; the store reaches it
 * through `setNotificationDeadLinkHandler` and waits until OK (or Escape / the backdrop).
 */
import { onBeforeUnmount, onMounted, ref } from 'vue'
import BaseModal from '@/components/feedback/BaseModal.vue'
import BaseButton from '@/components/navigation/BaseButton.vue'
import { OVERLAY_Z_CONFIRM_DIALOG } from '@/lib/ui/overlayLayers'
import { deadLinkCopy, type DeadLinkCopy } from '@/lib/notifications/deadLink'
import { setNotificationDeadLinkHandler } from '@/stores/notificationsStore'
import type { NotificationTarget } from '@/services/notificationsApi'

const open = ref(false)
const copy = ref<DeadLinkCopy>({ title: '', message: '' })
let settle: (() => void) | null = null

function show(target: NotificationTarget): Promise<void> {
  // A second dead link while one is up: the first is done with, the new one shows.
  settle?.()
  copy.value = deadLinkCopy(target)
  open.value = true
  return new Promise<void>((resolve) => {
    settle = resolve
  })
}

function close(): void {
  open.value = false
  const done = settle
  settle = null
  done?.()
}

function onModel(value: boolean): void {
  if (!value) close()
}

onMounted(() => {
  setNotificationDeadLinkHandler(show)
})

onBeforeUnmount(() => {
  close()
  setNotificationDeadLinkHandler(async () => {})
})
</script>
